<template>
  <div class="content">
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-7">
          <h6 class="h2 text-white d-inline-block mb-0">
            {{ $t("modern_trade.doc_format.title") }}
          </h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb></route-bread-crumb>
          </nav>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--6">
      <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
        <div class="px-4 pt-3">
          <p class="text-muted small mb-3">{{ $t("modern_trade.doc_format.subtitle") }}</p>

          <!-- Doc type selector -->
          <div class="form-group">
            <label class="form-control-label mr-3">{{ $t("modern_trade.doc_format.doc_type") }}</label>
            <el-radio-group v-model="docType" size="small" @change="loadConfig">
              <el-radio-button label="production">
                {{ $t("modern_trade.doc_format.doc_type_production") }}
              </el-radio-button>
              <el-radio-button label="delivery">
                {{ $t("modern_trade.doc_format.doc_type_delivery") }}
              </el-radio-button>
              <el-radio-button label="invoice">
                {{ $t("modern_trade.doc_format.doc_type_invoice") }}
              </el-radio-button>
            </el-radio-group>
          </div>

          <div v-loading="loading">
            <!-- ── Appearance ── -->
            <h6 class="heading-small text-muted mt-4 mb-3">
              {{ $t("modern_trade.doc_format.section_appearance") }}
            </h6>
            <div class="row">
              <div class="col-md-4">
                <div class="form-group">
                  <label class="form-control-label d-block">{{ $t("modern_trade.doc_format.header_color") }}</label>
                  <el-color-picker v-model="config.color"></el-color-picker>
                </div>
              </div>
              <div class="col-md-4">
                <div class="form-group">
                  <label class="form-control-label d-block">{{ $t("modern_trade.doc_format.show_logo") }}</label>
                  <el-switch v-model="config.show_logo"></el-switch>
                </div>
              </div>
              <div class="col-md-4">
                <div class="form-group">
                  <label class="form-control-label d-block">{{ $t("modern_trade.doc_format.show_stamp") }}</label>
                  <el-switch v-model="config.show_stamp"></el-switch>
                </div>
              </div>
            </div>

            <!-- ── Header fields ── -->
            <h6 class="heading-small text-muted mt-3 mb-3">
              {{ $t("modern_trade.doc_format.section_header_fields") }}
            </h6>
            <table class="table table-sm align-items-center mb-4">
              <thead class="thead-light">
                <tr>
                  <th style="width: 90px">{{ $t("modern_trade.doc_format.col_show") }}</th>
                  <th style="width: 200px">{{ $t("modern_trade.doc_format.field") }}</th>
                  <th>{{ $t("modern_trade.doc_format.col_label") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="k in fieldKeys" v-if="config.fields[k]" :key="'f_' + k">
                  <td><el-checkbox v-model="config.fields[k].show"></el-checkbox></td>
                  <td>{{ fieldTitle(k) }}</td>
                  <td>
                    <el-input
                      v-model="config.fields[k].label"
                      size="small"
                      :placeholder="fieldTitle(k)"
                      :disabled="!config.fields[k].show"
                    ></el-input>
                  </td>
                </tr>
              </tbody>
            </table>

            <!-- ── Product columns ── -->
            <h6 class="heading-small text-muted mt-3 mb-3">
              {{ $t("modern_trade.doc_format.section_columns") }}
            </h6>
            <!-- #304: ใบกำกับภาษีรวม "รหัสสินค้า" + "รายการ" เป็นคอลัมน์เดียวแล้ว -->
            <p v-if="isInvoice" class="text-muted small mb-2">
              <i class="fa fa-info-circle"></i> {{ $t("modern_trade.doc_format.invoice_merged_hint") }}
            </p>
            <table class="table table-sm align-items-center mb-4">
              <thead class="thead-light">
                <tr>
                  <th style="width: 90px">{{ $t("modern_trade.doc_format.col_show") }}</th>
                  <th style="width: 240px">{{ $t("modern_trade.doc_format.column") }}</th>
                  <th>{{ $t("modern_trade.doc_format.col_label") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="k in columnKeys" v-if="config.columns[k]" :key="'c_' + k">
                  <td><el-checkbox v-model="config.columns[k].show"></el-checkbox></td>
                  <td>{{ columnTitle(k) }}</td>
                  <td>
                    <!-- IV: `code` ไม่ใช่คอลัมน์อีกต่อไป — เหลือแค่ toggle "แสดงรหัสนำหน้า"
                         (BE mtInvoiceItemLabel อ่านเฉพาะ show, ไม่อ่าน label) -->
                    <small v-if="isCodePrefixRow(k)" class="text-muted">
                      {{ $t("modern_trade.doc_format.c_code_invoice_hint") }}
                    </small>
                    <el-input
                      v-else
                      v-model="config.columns[k].label"
                      size="small"
                      :placeholder="columnTitle(k)"
                      :disabled="!config.columns[k].show"
                    ></el-input>
                  </td>
                </tr>
              </tbody>
            </table>

            <!-- ── Summary (ใบสั่งผลิตไม่มีแถวสรุปยอด) ── -->
            <template v-if="hasSummary">
              <h6 class="heading-small text-muted mt-3 mb-3">
                {{ $t("modern_trade.doc_format.section_summary") }}
              </h6>
              <div class="form-group">
                <label class="form-control-label mr-3">{{ $t("modern_trade.doc_format.show_summary") }}</label>
                <el-switch v-model="config.summary.show"></el-switch>
              </div>
            </template>

            <!-- ── Footer ── -->
            <h6 class="heading-small text-muted mt-3 mb-3">
              {{ $t("modern_trade.doc_format.section_footer") }}
            </h6>
            <div class="form-group">
              <label class="form-control-label">{{ $t("modern_trade.doc_format.footer_note") }}</label>
              <el-input
                v-model="config.footer_note"
                type="textarea"
                :rows="3"
              ></el-input>
            </div>
            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label class="form-control-label">{{ $t("modern_trade.doc_format.sign_left_label") }}</label>
                  <el-input v-model="config.sign_left_label"></el-input>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label class="form-control-label">{{ $t("modern_trade.doc_format.sign_right_label") }}</label>
                  <el-input v-model="config.sign_right_label"></el-input>
                </div>
              </div>
            </div>

            <div class="d-flex justify-content-end mt-3">
              <el-button @click="resetDefaults">{{ $t("modern_trade.doc_format.reset") }}</el-button>
              <el-button
                v-if="isEditModernTrade"
                type="primary"
                :loading="saving"
                @click="handleSave"
              >{{ $t("modern_trade.doc_format.save") }}</el-button>
            </div>
          </div>
        </div>
      </card>
    </div>
  </div>
</template>

<script>
import {
  RadioGroup,
  RadioButton,
  ColorPicker,
  Switch,
  Checkbox,
  Input,
  Button,
  Loading,
} from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import swal from "sweetalert2";
import { mapGetters } from "vuex";
import { ModernTradeService } from "@/resource";

// ── Key maps (must mirror the backend PDF renderers exactly) ──────────────────
// A show/rename override only does something when the key is one the renderer
// actually asks for (cfg.FieldShow / cfg.ColShow / cfg.SummaryShow), so the UI
// lists the real keys of each document:
//   production → MT-ProductionOrder.go  (fields doc_no/date/po_ref;
//                columns no/code/name/size/qty/unit; no summary rows)
//   delivery   → MT-Delivery.go         (fields do_no/date/po_number/ship_method/
//                tracking; columns no/code/barcode/name/lot/exp/qty/unit_price/amount)
//   invoice    → MT-Invoice.go          (fields doc_no/date/credit_term/po_number/
//                ship_method; columns no/barcode/name/qty/unit_price/amount)
//
// Nov-5 §1: `barcode` (wms_product.barcode) is a real column on the DO and IV
// PDFs, shown by default; blank on rows whose product has no barcode.
//
// #304: the invoice merged "รหัสสินค้า" + "รายการ" into ONE column keyed `name`
// ("P-075 - ชื่อสินค้า"). `code` survives ONLY as a boolean: mtInvoiceItemLabel()
// reads columns.code.show to decide whether the code prefix is printed and never
// reads columns.code.label. The UI therefore renders it as a toggle-only row.
const DOC_FIELD_KEYS = {
  production: ["doc_no", "date", "po_ref"],
  delivery: ["do_no", "date", "po_number", "ship_method", "tracking"],
  invoice: ["doc_no", "date", "credit_term", "po_number", "ship_method"],
};
const DOC_COLUMN_KEYS = {
  production: ["no", "code", "name", "size", "qty", "unit"],
  delivery: ["no", "code", "barcode", "name", "lot", "exp", "qty", "unit_price", "amount"],
  // `code` = prefix toggle (see above), rendered before the merged `name` column.
  invoice: ["no", "barcode", "code", "name", "qty", "unit_price", "amount"],
};
// Footer summary rows the backend can show/hide per key (MtDocFormatConfig.Summary
// is a map, not a single flag). The UI exposes one "show summary" toggle, so on
// save we either send an empty map (all default-visible) or set every key hidden.
const DOC_SUMMARY_KEYS = {
  production: [], // MO has no totals block at all
  delivery: ["subtotal", "vat", "net"],
  invoice: ["subtotal", "discount", "after_discount", "vat"],
};
const ALL_SUMMARY_KEYS = ["subtotal", "discount", "after_discount", "vat", "net"];

export default {
  name: "MtDocFormatSettings",
  components: {
    RouteBreadCrumb,
    [RadioGroup.name]: RadioGroup,
    [RadioButton.name]: RadioButton,
    [ColorPicker.name]: ColorPicker,
    [Switch.name]: Switch,
    [Checkbox.name]: Checkbox,
    [Input.name]: Input,
    [Button.name]: Button,
  },
  directives: {
    loading: Loading.directive,
  },
  computed: {
    ...mapGetters({
      isEditModernTrade: "Auth/isEditModernTrade",
    }),
    isInvoice() {
      return this.docType === "invoice";
    },
    fieldKeys() {
      return DOC_FIELD_KEYS[this.docType] || [];
    },
    columnKeys() {
      return DOC_COLUMN_KEYS[this.docType] || [];
    },
    summaryKeys() {
      return DOC_SUMMARY_KEYS[this.docType] || [];
    },
    hasSummary() {
      return this.summaryKeys.length > 0;
    },
  },
  data() {
    return {
      docType: "production",
      loading: false,
      saving: false,
      config: this.buildDefaultConfig("production"),
    };
  },
  mounted() {
    this.loadConfig();
  },
  methods: {
    // Row title: `code` on the invoice is a prefix toggle, not a column.
    columnTitle(k) {
      if (this.isCodePrefixRow(k)) return this.$t("modern_trade.doc_format.c_code_invoice");
      return this.$t("modern_trade.doc_format.c_" + k);
    },
    fieldTitle(k) {
      return this.$t("modern_trade.doc_format.f_" + k);
    },
    isCodePrefixRow(k) {
      return this.isInvoice && k === "code";
    },
    buildDefaultConfig(docType) {
      const fields = {};
      (DOC_FIELD_KEYS[docType] || []).forEach(k => {
        fields[k] = { show: true, label: "" };
      });
      const columns = {};
      (DOC_COLUMN_KEYS[docType] || []).forEach(k => {
        columns[k] = { show: true, label: "" };
      });
      return {
        color: "#1f6feb",
        show_logo: true,
        show_stamp: true,
        fields,
        columns,
        summary: { show: true },
        footer_note: "",
        sign_left_label: "",
        sign_right_label: "",
      };
    },
    // Merge a partial config coming from the API onto a fresh default so every
    // key the template touches is always present (avoids undefined access).
    //
    // Backward-compat: keys the current UI no longer renders (a legacy key from an
    // older key set, e.g. `doc_date` / `discount`) are copied through untouched so
    // saving from this page never drops what the user had stored.
    normalizeConfig(raw) {
      const base = this.buildDefaultConfig(this.docType);
      if (!raw || typeof raw !== "object") return base;
      // BE field is `header_color`; keep the FE-internal name `color`.
      if (typeof raw.header_color === "string" && raw.header_color) base.color = raw.header_color;
      if (typeof raw.show_logo === "boolean") base.show_logo = raw.show_logo;
      if (typeof raw.show_stamp === "boolean") base.show_stamp = raw.show_stamp;
      const mergeMap = (target, src) => {
        if (!src || typeof src !== "object") return;
        Object.keys(src).forEach(k => {
          const v = src[k];
          if (!v || typeof v !== "object") return;
          target[k] = {
            show: v.show !== false,
            label: typeof v.label === "string" ? v.label : "",
          };
        });
      };
      mergeMap(base.fields, raw.fields);
      mergeMap(base.columns, raw.columns);
      // BE summary is a per-row map ({subtotal:{show},...}); the UI is one toggle.
      // Off only when a known summary row was explicitly hidden.
      if (raw.summary && typeof raw.summary === "object") {
        base.summary.show = !ALL_SUMMARY_KEYS.some(
          k => raw.summary[k] && raw.summary[k].show === false
        );
      }
      if (typeof raw.footer_note === "string") base.footer_note = raw.footer_note;
      if (typeof raw.sign_left_label === "string") base.sign_left_label = raw.sign_left_label;
      if (typeof raw.sign_right_label === "string") base.sign_right_label = raw.sign_right_label;
      return base;
    },
    async loadConfig() {
      this.loading = true;
      // #166 el-radio-group @change fires synchronously — docType (and therefore
      // the fieldKeys/columnKeys computed) switches to the new doc type's key set
      // immediately, but `this.config` is still built from the OLD key set until
      // this await resolves. Reset it to the new doc type's defaults right away so
      // `config.fields[k]` / `config.columns[k]` are never undefined mid-request
      // (v-for `k in fieldKeys` would otherwise read `.show` off `undefined`).
      this.config = this.buildDefaultConfig(this.docType);
      try {
        const res = await ModernTradeService.docFormatGet({ doc_type: this.docType });
        const d = (res.data && res.data.Data && res.data.Data[0]) || {};
        this.config = this.normalizeConfig(d.config);
      } catch (err) {
        // No saved config yet is fine — fall back to defaults.
        this.config = this.buildDefaultConfig(this.docType);
        if (err.response && err.response.status && err.response.status !== 404) {
          swal.fire({ title: this.errMsg(err), icon: "error" });
        }
      } finally {
        this.loading = false;
      }
    },
    resetDefaults() {
      this.config = this.buildDefaultConfig(this.docType);
    },
    // Map the FE-internal config onto the exact JSON shape the backend
    // (controllers.MtDocFormatConfig) expects: `header_color` string, and
    // `summary` as a per-row show/hide map rather than a single boolean.
    serializeConfig() {
      const summary = {};
      if (this.hasSummary && !this.config.summary.show) {
        ALL_SUMMARY_KEYS.forEach(k => {
          summary[k] = { show: false };
        });
      }
      return {
        header_color: this.config.color || "",
        show_logo: this.config.show_logo,
        show_stamp: this.config.show_stamp,
        fields: this.config.fields,
        columns: this.config.columns,
        summary,
        footer_note: this.config.footer_note,
        sign_left_label: this.config.sign_left_label,
        sign_right_label: this.config.sign_right_label,
      };
    },
    async handleSave() {
      this.saving = true;
      try {
        await ModernTradeService.docFormatSave({
          doc_type: this.docType,
          config: this.serializeConfig(),
        });
        const result = await swal.fire({
          title: this.$t("modern_trade.doc_format.saved"),
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });
        // sweetalert2 v9: dismissed timers still resolve — result.value guards
        // an explicit confirm click when a button is shown.
        void result;
      } catch (err) {
        swal.fire({ title: this.errMsg(err) || this.$t("modern_trade.doc_format.save_error"), icon: "error" });
      } finally {
        this.saving = false;
      }
    },
    errMsg(err) {
      return (err.response && err.response.data && err.response.data.Cause) || err.message;
    },
  },
};
</script>
